export function stripNumericTrailingSegment(pathname: string): string {
  const segments = pathname
    .split('/')
    .filter((segment) => segment.trim().length > 0);

  if (segments.length > 0 && /^\d+$/.test(segments[segments.length - 1])) {
    segments.pop();
  }

  if (segments.length === 0) {
    return '/';
  }

  return `/${segments.join('/')}`;
}

export type UrlLocation = Pick<Location, 'origin' | 'pathname'> & Partial<Pick<Location, 'search' | 'hostname'>>;

let headerHrefOrigin: string | null = null;

function normalizeHrefOrigin(value: string | null | undefined): string | null {
  if (!value?.trim()) {
    return null;
  }

  try {
    const url = new URL(value.trim());
    if (['http:', 'https:'].includes(url.protocol) && !url.username && !url.password) {
      let pathname = url.pathname;
      while (pathname.endsWith('/')) {
        pathname = pathname.slice(0, -1);
      }
      return `${url.origin}${pathname}`;
    }
  } catch {
    return null;
  }

  return null;
}

function queryHrefOrigin(loc: UrlLocation | undefined): string | null {
  return normalizeHrefOrigin(new URLSearchParams(loc?.search).get('hrefOrigin'));
}

export async function initializeHrefOrigin(
  loc: UrlLocation = globalThis.location,
  request: typeof fetch = globalThis.fetch,
): Promise<void> {
  headerHrefOrigin = null;
  if (queryHrefOrigin(loc) || !loc?.origin || loc.origin === 'null' || typeof request !== 'function') {
    return;
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 3000);
  try {
    const response = await request(`${loc.origin}${loc.pathname}${loc.search ?? ''}`, {
      method: 'HEAD',
      credentials: 'same-origin',
      cache: 'no-store',
      redirect: 'error',
      signal: controller.signal,
    });
    if (response.ok) {
      headerHrefOrigin = normalizeHrefOrigin(response.headers.get('hrefOrigin'));
    }
  } catch {
    headerHrefOrigin = null;
  } finally {
    clearTimeout(timeoutId);
  }
}

export function buildOriginBaseUrl(
  loc: UrlLocation = globalThis.location,
): string {
  const hrefOrigin = queryHrefOrigin(loc) ?? headerHrefOrigin;
  if (hrefOrigin) {
    return hrefOrigin;
  }

  if (!loc?.origin || loc.origin === 'null') {
    return '';
  }

  const basePath = stripNumericTrailingSegment(loc.pathname);
  if (basePath === '/') {
    return loc.origin;
  }

  return `${loc.origin}${basePath}`;
}

export function buildSameOriginApiUrl(
  suffixPath: string,
  loc: UrlLocation = globalThis.location,
): string {
  const sanitizedSuffix = suffixPath.startsWith('/') ? suffixPath : `/${suffixPath}`;
  return `${buildOriginBaseUrl(loc)}${sanitizedSuffix}`;
}
